import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Spinner, Lightning, ArrowsClockwise, Files, Upload, FileText, NotePencil, CaretDown, CaretRight, Calendar, UsersThree, Warning, EnvelopeSimple, Copy, ArrowUUpLeft, ChatText, PaperPlaneTilt, Trash, MagnifyingGlass } from '@phosphor-icons/react';

const ACCEPT = '.pdf,.docx,.doc,.txt,.csv,.md,.html,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff,.heic,.heif';

const STATUS_MAP = {
  uploaded: { label: 'Uploaded', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  processing: { label: 'Processing', color: 'bg-blue-50 text-blue-600 border-blue-200' },
  analyzed: { label: 'Analyzed', color: 'bg-green-50 text-green-700 border-green-200' },
  error: { label: 'Error', color: 'bg-red-50 text-red-700 border-red-200' },
  skipped_duplicate: { label: 'Duplicate — skipped', color: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const hasNotes = (n) => n && (n.one_line || (n.key_facts || []).length || (n.key_dates || []).length);

const DocComments = ({ d, loadAll, onCountChange }) => {
  const [comments, setComments] = useState(d.ai_comments || []);
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const [reevaluating, setReevaluating] = useState(false);

  const syncCount = (next) => { setComments(next); onCountChange?.(next.length); };

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/cases/${d.case_id}/documents/${d._id}/comments`, { text: t });
      syncCount([...comments, data.comment]);
      setText('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add note');
    } finally { setAdding(false); }
  };

  const remove = async (cid) => {
    const prev = comments;
    syncCount(comments.filter(c => c.id !== cid));
    try { await api.delete(`/cases/${d.case_id}/documents/${d._id}/comments/${cid}`); }
    catch (err) {
      syncCount(prev);
      alert(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const reevaluate = async () => {
    setReevaluating(true);
    try {
      await api.post(`/cases/${d.case_id}/documents/${d._id}/reevaluate`);
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to re-evaluate');
    } finally { setReevaluating(false); }
  };

  return (
    <div className="px-4 pb-4 pt-1 border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid={`doc-comments-panel-${d._id}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm mt-3 mb-2 flex items-center gap-1">
        <ChatText size={12} /> Notes to the AI
      </p>
      <p className="text-xs text-[#6B7280] font-ibm mb-2">
        Tell the AI what it might miss — e.g. "The printed date is a form revision; the incident actually occurred on March 3, 2023" or "Mr. Smith is the property manager." These notes are treated as authoritative on re-evaluation.
      </p>
      {comments.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {comments.map(c => (
            <div key={c.id} className="flex items-start gap-2 bg-white border border-[#E5E7EB] rounded-md px-2.5 py-1.5" data-testid={`doc-comment-${c.id}`}>
              <ChatText size={13} className="text-[#002FA7] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-ibm text-[#09090B]">{c.text}</p>
                <p className="text-[10px] text-[#6B7280] font-ibm">{c.author} · {new Date(c.created_at).toLocaleString()}</p>
              </div>
              <button onClick={() => remove(c.id)} className="text-[#6B7280] hover:text-red-600 shrink-0" data-testid={`doc-comment-delete-${c.id}`}><Trash size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && text.trim() && !adding) add(); }}
          placeholder="Add a note for the AI…" data-testid={`doc-comment-input-${d._id}`}
          className="flex-1 px-3 py-2 border border-[#E5E7EB] rounded-md text-sm font-ibm focus:outline-none focus:border-[#002FA7]" />
        <button onClick={add} disabled={adding || !text.trim()} data-testid={`doc-comment-add-${d._id}`}
          className="flex items-center gap-1 px-3 py-2 bg-white border border-[#002FA7] text-[#002FA7] text-xs font-ibm font-semibold rounded-md hover:bg-[#002FA7]/5 transition-all disabled:opacity-50">
          {adding ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} />} Add
        </button>
      </div>
      <button onClick={reevaluate} disabled={reevaluating} data-testid={`doc-reevaluate-${d._id}`}
        className="mt-2 flex items-center gap-1.5 px-3 py-2 bg-[#002FA7] text-white text-xs font-ibm font-semibold rounded-md hover:bg-[#00237D] transition-all disabled:opacity-50">
        {reevaluating ? <><Spinner size={13} className="animate-spin" /> Re-evaluating…</> : <><MagnifyingGlass size={13} /> Re-evaluate with notes</>}
      </button>
    </div>
  );
};

const DocNotes = ({ notes }) => (
  <div className="px-4 pb-4 pt-1 border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid="doc-notes-panel">
    {notes.one_line && <p className="text-sm font-ibm text-[#09090B] mt-3">{notes.one_line}</p>}
    {notes.doc_type && (
      <span className="inline-block mt-2 text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7]">{notes.doc_type}</span>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
      {(notes.key_dates || []).length > 0 && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm flex items-center gap-1 mb-1"><Calendar size={12} /> Key Dates</p>
          <ul className="space-y-0.5">{notes.key_dates.map((d, i) => <li key={`kd-${i}`} className="text-xs font-ibm text-[#09090B]">{d}</li>)}</ul>
        </div>
      )}
      {(notes.people || []).length > 0 && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm flex items-center gap-1 mb-1"><UsersThree size={12} /> People</p>
          <ul className="space-y-0.5">{notes.people.map((p, i) => <li key={`pp-${i}`} className="text-xs font-ibm text-[#09090B]">{p}</li>)}</ul>
        </div>
      )}
    </div>
    {(notes.key_facts || []).length > 0 && (
      <div className="mt-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm mb-1">Key Facts</p>
        <ul className="list-disc pl-5 space-y-0.5">{notes.key_facts.map((f, i) => <li key={`kf-${i}`} className="text-xs font-ibm text-[#09090B]">{f}</li>)}</ul>
      </div>
    )}
    {(notes.potential_issues || []).length > 0 && (
      <div className="mt-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 font-ibm flex items-center gap-1 mb-1"><Warning size={12} /> Potential Issues</p>
        <ul className="list-disc pl-5 space-y-0.5">{notes.potential_issues.map((p, i) => <li key={`pi-${i}`} className="text-xs font-ibm text-amber-800">{p}</li>)}</ul>
      </div>
    )}
    {notes.relevance && (
      <p className="text-xs font-ibm text-[#6B7280] italic mt-3">Relevance: {notes.relevance}</p>
    )}
  </div>
);

const EmailMessages = ({ messages }) => (
  <div className="px-4 pb-4 pt-1 border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid="email-messages-panel">
    <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] font-ibm mt-3 mb-2 flex items-center gap-1">
      <EnvelopeSimple size={12} /> Email Chain — {messages.length} message{messages.length !== 1 ? 's' : ''}
    </p>
    <div className="space-y-2">
      {messages.map((m, i) => (
        <div key={`em-${i}`} className={`text-xs font-ibm border rounded-md p-2.5 ${m.is_duplicate ? 'bg-gray-100 border-gray-200 opacity-70' : 'bg-white border-[#E5E7EB]'}`}
          data-testid={`email-message-${i}`}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-semibold text-[#09090B] truncate">{m.from || 'Unknown sender'}</span>
            <div className="flex items-center gap-2 shrink-0">
              {m.date && <span className="text-[#6B7280]">{m.date}</span>}
              {m.is_duplicate
                ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">Duplicate</span>
                : <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-green-100 text-green-700">New</span>}
            </div>
          </div>
          {m.subject && <p className="text-[#6B7280] mb-0.5">Subject: {m.subject}</p>}
          <p className="text-[#09090B] line-clamp-3 whitespace-pre-wrap">{m.content}</p>
        </div>
      ))}
    </div>
  </div>
);

const DocItem = ({ d, analyzing, analyzeDoc, loadAll }) => {
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [togglingDup, setTogglingDup] = useState(false);
  const [commentCount, setCommentCount] = useState((d.ai_comments || []).length);
  const st = STATUS_MAP[d.analysis_status] || STATUS_MAP.uploaded;
  const noteReady = hasNotes(d.notes);
  const messages = d.email_messages || [];
  const isEmailChain = d.is_email_chain && messages.length > 0;
  const dupMsgCount = messages.filter(m => m.is_duplicate).length;

  const retryExtract = async () => {
    setRetrying(true);
    try {
      await api.post(`/documents/${d._id}/retry-extract`);
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const toggleDuplicate = async () => {
    setTogglingDup(true);
    try {
      await api.post(`/cases/${d.case_id}/documents/${d._id}/duplicate`, { is_duplicate: !d.is_duplicate });
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update duplicate status');
    } finally {
      setTogglingDup(false);
    }
  };

  return (
    <div className={`bg-white border rounded-lg hover:shadow-sm transition-all ${d.is_duplicate ? 'border-gray-300 bg-gray-50/60' : 'border-[#E5E7EB]'}`} data-testid={`doc-${d._id}`}>
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isEmailChain ? <EnvelopeSimple size={24} className="text-[#002FA7] shrink-0" /> : <FileText size={24} className="text-[#002FA7] shrink-0" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-ibm font-semibold text-[#09090B] text-sm truncate">{d.file_name}</p>
              {d.is_duplicate && (
                <span data-testid={`duplicate-badge-${d._id}`} className="shrink-0 text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-200 text-gray-600 flex items-center gap-1">
                  <Copy size={10} /> Duplicate
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-xs text-[#6B7280] font-ibm">{d.file_type?.toUpperCase()}</span>
              <span className="text-xs text-[#6B7280] font-ibm">{(d.file_size_bytes / 1024).toFixed(1)} KB</span>
              {d.entity_count > 0 && <span className="text-xs text-[#6B7280] font-ibm">{d.entity_count} entities</span>}
              {d.claim_count > 0 && <span className="text-xs text-[#6B7280] font-ibm">{d.claim_count} claims</span>}
              {isEmailChain && (
                <span className="text-xs text-[#6B7280] font-ibm">{messages.length - dupMsgCount} new / {dupMsgCount} duplicate msg</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isEmailChain && (
            <button onClick={() => setEmailOpen(o => !o)} data-testid={`toggle-email-${d._id}`}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-ibm font-semibold text-[#002FA7] border border-[#002FA7]/30 rounded-md hover:bg-[#002FA7]/5 transition-all">
              <EnvelopeSimple size={12} /> Messages {emailOpen ? <CaretDown size={11} /> : <CaretRight size={11} />}
            </button>
          )}
          {noteReady && (
            <button onClick={() => setOpen(o => !o)} data-testid={`toggle-notes-${d._id}`}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-ibm font-semibold text-[#002FA7] border border-[#002FA7]/30 rounded-md hover:bg-[#002FA7]/5 transition-all">
              <NotePencil size={12} /> Notes {open ? <CaretDown size={11} /> : <CaretRight size={11} />}
            </button>
          )}
          <button onClick={() => setCommentsOpen(o => !o)} data-testid={`toggle-comments-${d._id}`}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-ibm font-semibold text-[#002FA7] border border-[#002FA7]/30 rounded-md hover:bg-[#002FA7]/5 transition-all">
            <ChatText size={12} /> Notes to AI{commentCount > 0 ? ` (${commentCount})` : ''} {commentsOpen ? <CaretDown size={11} /> : <CaretRight size={11} />}
          </button>
          <span className={`text-xs font-ibm font-semibold px-2 py-0.5 rounded border ${st.color}`}>
            {d.analysis_status === 'processing' && <Spinner size={10} className="animate-spin inline mr-1" />}
            {st.label}
          </span>
          <button data-testid={`toggle-duplicate-${d._id}`} onClick={toggleDuplicate} disabled={togglingDup}
            title={d.is_duplicate ? 'Include this document in AI analysis & timeline' : 'Exclude this document from AI analysis & timeline'}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-ibm font-semibold rounded-md transition-all disabled:opacity-50 ${d.is_duplicate ? 'bg-[#002FA7] text-white hover:bg-[#00237D]' : 'border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
            {togglingDup ? <Spinner size={12} className="animate-spin" /> : (d.is_duplicate ? <ArrowUUpLeft size={12} /> : <Copy size={12} />)}
            {d.is_duplicate ? 'Mark as Not-Duplicate' : 'Mark as Duplicate'}
          </button>
          {d.analysis_status === 'uploaded' && (
            <button data-testid={`analyze-doc-${d._id}`} onClick={() => analyzeDoc(d._id)} disabled={analyzing === d._id}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] disabled:opacity-50 transition-all">
              {analyzing === d._id ? <><Spinner size={12} className="animate-spin" /> Analyzing...</> : <><Lightning size={12} /> Analyze</>}
            </button>
          )}
          {d.analysis_status === 'error' && (
            <button data-testid={`retry-doc-${d._id}`} onClick={retryExtract} disabled={retrying}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-orange-500 text-white font-ibm font-semibold rounded-md hover:bg-orange-600 disabled:opacity-50 transition-all">
              {retrying ? <><Spinner size={12} className="animate-spin" /> OCR...</> : <><ArrowsClockwise size={12} /> Retry OCR</>}
            </button>
          )}
        </div>
      </div>
      {emailOpen && isEmailChain && <EmailMessages messages={messages} />}
      {open && noteReady && <DocNotes notes={d.notes} />}
      {commentsOpen && <DocComments d={d} loadAll={loadAll} onCountChange={setCommentCount} />}
    </div>
  );
};

export const DocumentsTab = ({
  documents, uploading, bulkUploading, analyzing, batchAnalyzing, generatingNotes,
  fileInputRef, bulkInputRef, uploadFile, bulkUpload, analyzeDoc, batchAnalyze, generateNotes, loadAll,
}) => {
  const pendingCount = documents.filter(d => d.analysis_status === 'uploaded').length;
  const analyzedCount = documents.filter(d => d.analysis_status === 'analyzed').length;

  return (
    <div data-testid="documents-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B]">Document Library</h3>
        <div className="flex gap-2 flex-wrap">
          {pendingCount > 0 && (
            <button data-testid="analyze-all-button" onClick={() => batchAnalyze(false)} disabled={batchAnalyzing}
              className="flex items-center gap-2 px-3 py-2 border border-[#002FA7] text-[#002FA7] font-ibm text-sm font-semibold rounded-md hover:bg-[#002FA7]/5 transition-all disabled:opacity-50">
              {batchAnalyzing ? <><Spinner size={14} className="animate-spin" /> Analyzing All...</> : <><Lightning size={14} /> Analyze All ({pendingCount})</>}
            </button>
          )}
          {analyzedCount > 0 && (
            <button data-testid="reanalyze-all-button" onClick={() => batchAnalyze(true)} disabled={batchAnalyzing}
              className="flex items-center gap-2 px-3 py-2 border border-[#FF9500] text-[#FF9500] font-ibm text-sm font-semibold rounded-md hover:bg-[#FF9500]/5 transition-all disabled:opacity-50"
              title="Re-run AI extraction on already-analyzed documents. Existing timeline entries for these documents will be replaced.">
              {batchAnalyzing ? <><Spinner size={14} className="animate-spin" /> Reanalyzing...</> : <><ArrowsClockwise size={14} /> Reanalyze All ({analyzedCount})</>}
            </button>
          )}
          {analyzedCount > 0 && (
            <button data-testid="generate-notes-button" onClick={generateNotes} disabled={generatingNotes}
              className="flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] text-[#09090B] font-ibm text-sm font-semibold rounded-md hover:bg-[#F3F4F6] transition-all disabled:opacity-50"
              title="Build/refresh concise, fact-only evidence notes for each document. These notes power the case analysis.">
              {generatingNotes ? <><Spinner size={14} className="animate-spin" /> Generating Notes...</> : <><NotePencil size={14} /> Generate Notes</>}
            </button>
          )}
          <label data-testid="bulk-upload-button"
            className={`flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] text-[#09090B] font-ibm text-sm rounded-md hover:bg-[#F3F4F6] cursor-pointer transition-all ${bulkUploading ? 'opacity-50' : ''}`}>
            {bulkUploading ? <><Spinner size={14} className="animate-spin" /> Uploading...</> : <><Files size={14} /> Bulk Upload</>}
            <input ref={bulkInputRef} type="file" className="hidden" onChange={bulkUpload} accept={ACCEPT} multiple disabled={bulkUploading} />
          </label>
          <label data-testid="upload-document-button"
            className={`flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold text-sm rounded-md hover:bg-[#00237D] cursor-pointer transition-all ${uploading ? 'opacity-50' : ''}`}>
            {uploading ? <><Spinner size={14} className="animate-spin" /> Uploading...</> : <><Upload size={14} /> Upload</>}
            <input ref={fileInputRef} type="file" className="hidden" onChange={uploadFile} accept={ACCEPT} disabled={uploading} />
          </label>
        </div>
      </div>
      {documents.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-[#E5E7EB] rounded-lg">
          <FileText size={48} className="mx-auto text-[#E5E7EB] mb-3" />
          <p className="text-[#6B7280] font-ibm">No documents uploaded yet. Upload PDF, DOCX, or TXT files to begin analysis.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(d => (
            <DocItem key={d._id} d={d} analyzing={analyzing} analyzeDoc={analyzeDoc} loadAll={loadAll} />
          ))}
        </div>
      )}
    </div>
  );
};
